import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import { InputPrimary } from "@/components/common/InputPrimary";
import { WebBuilderPreloader } from "@/components/webbuilder/ui/loaders/WebBuilderPreloader";
import { usePages } from "@/hooks/usePages";
import { Page } from "@/types/models/page";
import { useAppDispatch } from "@/store/hooks";
import { pageFormReset, submitPageRequest } from "@/store/slices/page.slice";
import { ArrowLeft, FileText, Link, Type, Image } from "lucide-react";
import { ImageUpload } from "@/components/webbuilder/ui/elements/ImageUpload";

export default function NewPagePage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { storeType, storeId } = useParams<{ storeType: string; storeId: string }>();
  const { form, pages } = usePages();
  const pendingSlugRef = useRef<string | null>(null);

  useEffect(() => {
    dispatch(pageFormReset());
  }, [dispatch]);

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    description: "",
    featured_image: "",
    featured_image_asset_id: "",
  });
  const isLoading = form?.loading ?? false;
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (form?.error) {
      setErrors((prev) => ({
        ...prev,
        submit: form.error.message || "Failed to create page. Please try again.",
      }));
    }
  }, [form?.error]);

  useEffect(() => {
    if (!form?.success || form.loading || !storeType || !storeId) return;

    const slug = pendingSlugRef.current;
    const created =
      (slug && pages?.find((p: Page) => p.slug === slug)) ?? pages?.[0];
    const newId = created?.id;

    pendingSlugRef.current = null;

    if (newId) {
      navigate(
        `/${storeType}/${storeId}/webbuilder/edit?pageId=${encodeURIComponent(newId)}`,
        { replace: true },
      );
      dispatch(pageFormReset());
      return;
    }

    navigate(`/${storeType}/${storeId}/webbuilder/edit`, { replace: true });
    dispatch(pageFormReset());
  }, [
    dispatch,
    form?.success,
    form.loading,
    navigate,
    pages,
    storeId,
    storeType,
  ]);

  // Auto-generate slug from title
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleTitleChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      title: value,
      slug:
        prev.slug === generateSlug(prev.title) || prev.slug === ""
          ? generateSlug(value)
          : prev.slug,
    }));
    if (errors.title) {
      setErrors((prev) => ({ ...prev, title: "" }));
    }
  };

  const handleSlugChange = (value: string) => {
    const cleanSlug = generateSlug(value);
    setFormData((prev) => ({ ...prev, slug: cleanSlug }));
    if (errors.slug) {
      setErrors((prev) => ({ ...prev, slug: "" }));
    }
  };

  const handleDescriptionChange = (value: string) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleImageSelected = (
    pageId: string,
    selectedFile: { assetId: string; url: string },
  ) => {
    setFormData((prev) => ({
      ...prev,
      featured_image: selectedFile.url,
      featured_image_asset_id: selectedFile.assetId,
    }));
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      featured_image: "",
      featured_image_asset_id: "",
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Page title is required";
    }

    if (!formData.slug.trim()) {
      newErrors.slug = "Page slug is required";
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug =
        "Slug can only contain lowercase letters, numbers, and hyphens";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    pendingSlugRef.current = formData.slug;
    setErrors((prev) => ({ ...prev, submit: "" }));

    dispatch(
      submitPageRequest({
        data: {
          name: formData.title,
          title: formData.title,
          slug: formData.slug,
          description: formData.description,
          featured_image: formData.featured_image || undefined,
          featured_image_asset_id: formData.featured_image_asset_id || undefined,
          status: "draft",
          sections: [],
        },
      }),
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      {/* Show preloader while creating page */}
      {isLoading && <WebBuilderPreloader message="Creating your new page..." />}

      <div className="h-screen w-full bg-gradient-page dark:bg-gradient-page-dark overflow-auto">
        <div className="max-w-2xl mx-auto px-8 py-12">
          {/* ...existing code... */}
          {/* Header */}
          <div className="flex items-center space-x-4 mb-8 animate-fadeInUp">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <div className="w-px h-6 bg-border"></div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Create New Page
              </h1>
              <p className="text-muted-foreground">
                Set up your new page with basic information
              </p>
            </div>
          </div>

          {/* Form */}
          <div
            className="bg-card dark:bg-auth-card rounded-xl border border-border dark:border-auth-background shadow-sm animate-fadeInUp"
            style={{ animationDelay: "0.1s" }}
          >
            <div className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Page Title */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-foreground mb-2">
                    <Type className="w-4 h-4" />
                    <span>Page Title</span>
                    <span className="text-destructive">*</span>
                  </label>
                  <InputPrimary
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="e.g., Home, About Us, Contact"
                    className={errors.title ? "border-destructive" : ""}
                  />
                  {errors.title && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.title}
                    </p>
                  )}
                </div>

                {/* Page Slug */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-foreground mb-2">
                    <Link className="w-4 h-4" />
                    <span>Page Slug (URL)</span>
                    <span className="text-destructive">*</span>
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 text-sm text-muted-foreground bg-muted border border-r-0 border-border rounded-l-lg">
                      /
                    </span>
                    <InputPrimary
                      type="text"
                      value={formData.slug}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      placeholder="page-url"
                      className={`rounded-l-none ${errors.slug ? "border-destructive" : ""}`}
                    />
                  </div>
                  {errors.slug && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.slug}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    This will be the URL path for your page
                  </p>
                </div>

                {/* Page Description */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-foreground mb-2">
                    <FileText className="w-4 h-4" />
                    <span>Page Description</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleDescriptionChange(e.target.value)}
                    placeholder="Brief description of what this page is about..."
                    rows={3}
                    className="w-full px-3 py-2 bg-background border border-border dark:border-auth-background rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This description helps with SEO and organization
                  </p>
                </div>

                {/* Featured Image */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-foreground mb-2">
                    <Image className="w-4 h-4" aria-hidden="true" />
                    <span>Featured Image</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Optional: Select an image that represents this page
                  </p>
                  <ImageUpload
                    pageId="new-page-form"
                    currentImage={formData.featured_image}
                    isUploading={false}
                    onImageSelected={handleImageSelected}
                    onRemoveImage={handleRemoveImage}
                  />
                </div>

                {/* Submit Error */}
                {errors.submit && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <p className="text-sm text-destructive">{errors.submit}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex space-x-4 pt-4">
                  <ButtonPrimary
                    type="submit"
                    disabled={isLoading}
                    className="flex-1"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creating Page...
                      </>
                    ) : (
                      "Create Page"
                    )}
                  </ButtonPrimary>
                  <ButtonOutline
                    type="button"
                    onClick={handleBack}
                    disabled={isLoading}
                  >
                    Cancel
                  </ButtonOutline>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
